import { BlurringLinks, CommercialRenderingLinks, ConstructionFilesLinks, DocumentationLinks, DroneLinks, DroneVideoLinks, EnergyCertificateLinks, FloorPlan3DLinks, FloorPlanCaptureLinks, FloorPlanCertificateLinks, GroundDroneVideoLinks, GroundLinks, GroundVideoLinks, HightDefinitionTourLinks, LandRegisterLinks, LawInquiriesLinks, MatterportFloorPlanLinks, MeasurementOnSiteLinks, MunicipalLinks, PhotoExtractionLinks, PrestigeLinks, PrestigeVideoLinks, PRODUCT_DETAILS_PAGE_SECTIONS, ProtectionLinks, Rendering360Links, ResidentialRenderingLinks, SlideshowLinks, Staging360Links, StandardStagingLinks, TeaserVideoLinks } from 'constants/staticLinks'
import { DocumentSubtype, ProductKind, ProductType } from 'constants/product'
import { Language } from 'translations/Language'
import { Nullable } from 'models/helpers'
import { coerceLanguage } from './coerceLanguage'
import { logger } from 'utils/logging'

export const getProductDetailsPage = (
  language: string,
  productKind: ProductKind,
  productType: ProductType,
  documentSubtype?: Nullable<DocumentSubtype>,
  productId?: Nullable<number>
) => {
  const links = getProductKindLinks(productKind, productType, documentSubtype)
  const page = getLocalizedPage(links, language)
  if (!page) return null

  const section = productId ? PRODUCT_DETAILS_PAGE_SECTIONS[productId] : undefined

  return section ? `${page}#${section}` : page
}

interface ProductKindLinks {
  [key: string]: string
}

const getLocalizedPage = (links: ProductKindLinks, language: string): Nullable<string> => {
  // Region-tagged locales ('de-DE', 'nl-BE') reach here from the browser detector and from the
  // embed `lang` parameter, and must resolve to the page of their language, not to the English one
  const coercedLanguage = coerceLanguage(language)
  if (coercedLanguage in links) {
    return links[coercedLanguage]
  } else if (Language.EN in links) {
    return links.EN
  }

  return null
}

// A document product whose subtype does not resolve is a backend data gap, not a UI state: the
// subtype is the only thing that identifies which help page to link. Warned once per distinct value
// because getProductKindLinks runs inside the memoized product-card render.
const warnedUnresolvedDocumentSubtypes = new Set<string>()

const warnUnresolvedDocumentSubtype = (documentSubtype: Nullable<DocumentSubtype> | undefined) => {
  const key = documentSubtype ?? 'MISSING'
  if (warnedUnresolvedDocumentSubtypes.has(key)) return
  warnedUnresolvedDocumentSubtypes.add(key)

  logger.warn('No help link for authorities-document product: documentSubtype did not resolve', undefined, {
    context: {
      component: 'productDetails', metadata: { documentSubtype: key }
    },
  })
}

const getAuthoritiesDocumentLink = (documentSubtype: DocumentSubtype): ProductKindLinks => {
  switch (documentSubtype) {
    case DocumentSubtype.CONSTRUCTION_FILE:
      return ConstructionFilesLinks

    case DocumentSubtype.LAND_REGISTRATION:
      return LandRegisterLinks

    case DocumentSubtype.PROTECTION:
      return ProtectionLinks

    case DocumentSubtype.MUNICIPAL:
      return MunicipalLinks

    case DocumentSubtype.DOCUMENTATION:
      return DocumentationLinks

    case DocumentSubtype.LAW_INQUIRIES:
      return LawInquiriesLinks

    default:
      return {}
  }
}

export const getProductKindLinks = (
  productKind: ProductKind,
  productType: ProductType,
  documentSubtype?: Nullable<DocumentSubtype>
): ProductKindLinks => {
  switch (productKind) {
    case ProductKind.GROUND_PHOTO:
      return GroundLinks

    case ProductKind.DRONE_PHOTO:
      return DroneLinks

    case ProductKind.GROUND_PHOTO_PRESTIGE:
      return PrestigeLinks

    case ProductKind.STANDARD_GROUND_VIDEO:
      return GroundVideoLinks

    case ProductKind.STANDARD_GROUND_AND_DRONE_VIDEO:
      return GroundDroneVideoLinks

    case ProductKind.STANDARD_DRONE_VIDEO:
      return DroneVideoLinks

    case ProductKind.TEASER_VIDEO:
      return TeaserVideoLinks

    case ProductKind.PRESTIGE_GROUND_AND_DRONE_VIDEO:
      return PrestigeVideoLinks

    case ProductKind.MATTERPORT_PHOTO:
      return PhotoExtractionLinks

    case ProductKind.HD:
      return HightDefinitionTourLinks

    case ProductKind.SLIDESHOW:
      return SlideshowLinks

    case ProductKind.MOBILE_APPLICATION_FLOOR_PLAN:
      return FloorPlanCaptureLinks

    case ProductKind.MATTERPORT_FLOOR_PLAN_WITHOUT_VISIT:
    case ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT:
      return MatterportFloorPlanLinks

    case ProductKind.FLOOR_PLAN_EDITING_3D:
      return FloorPlan3DLinks

    case ProductKind.FLOOR_PLAN_CERTIFICATION:
      return FloorPlanCertificateLinks

    case ProductKind.RESIDENTIAL_STAGING:
    case ProductKind.COMMERCIAL_STAGING:
      if (productType === ProductType.PHOTO) {
        return StandardStagingLinks // type: PHOTO, kind: Residential staging + Commercial staging
      } else if (productType === ProductType.STAGING_360) {
        return Staging360Links // type: 360, kind: Residential staging + Commercial staging
      }
      return {}

    case ProductKind.COMMERCIAL_RENDERING:
      if (productType === ProductType.PHOTO) {
        return CommercialRenderingLinks // type: PHOTO, kind: Commercial Rendering
      } else if (productType === ProductType.STAGING_360) {
        return Rendering360Links // type: 360, kind: Commercial Rendering
      }
      return {}

    case ProductKind.RESIDENTIAL_RENDERING:
      if (productType === ProductType.PHOTO) {
        return ResidentialRenderingLinks // type: PHOTO, kind: Residential Rendering
      } else if (productType === ProductType.STAGING_360) {
        return Rendering360Links // type: 360, kind: Residential Rendering
      }
      return {}

    case ProductKind.AUTHORITIES_DOCUMENTS: {
      // The subtype is now the only source for the help link — ISSUE-2215 dropped the legacy type
      // this used to fall back to. It is absent only when Product2 carries no Document_Subtype__c
      // (traits-only under USE_PRODUCT2_TRAITS), which the backend counts as a sync anomaly, so the
      // caller renders no help link and we leave a breadcrumb rather than guessing a page
      const documentLinks = documentSubtype ? getAuthoritiesDocumentLink(documentSubtype) : {}
      if (Object.keys(documentLinks).length === 0) warnUnresolvedDocumentSubtype(documentSubtype)

      return documentLinks
    }

    case ProductKind.MEASUREMENT_ON_SITE:
      return MeasurementOnSiteLinks

    case ProductKind.PHOTO_BLURRING:
      return BlurringLinks

    case ProductKind.ENERGY_CERTIFICATE:
    case ProductKind.ENERGY_CERTIFICATE_OF_NEED:
    case ProductKind.CONSUMPTION_CERTIFICATE:
      return EnergyCertificateLinks

    default:
      return {}
  }
}
